import { useTranslation } from "react-i18next";
import { isRangeValue, type AttributeDefView, type AttributeValue, type RangeValue } from "../api/catalog";
import { formatDate } from "../lib/dates";
import { InfoPopover } from "./TextPopover";

type Translate = (key: string, fallback: string) => string;

/** "100 – 150 kW"; one end alone "≥ 100 kW" / "≤ 150 kW"; "—" with neither. */
export function formatRange(v: RangeValue, unit: string | null): string {
  const u = unit ? ` ${unit}` : "";
  if (v.min !== null && v.max !== null) return `${v.min} – ${v.max}${u}`;
  if (v.min !== null) return `≥ ${v.min}${u}`;
  if (v.max !== null) return `≤ ${v.max}${u}`;
  return "—";
}

/** Display text for one attribute value: number + unit, da/ne, date, a span as "min – max", or the raw text (a list value as such); "—" when empty. */
export function formatAttributeValue(def: AttributeDefView, value: AttributeValue | undefined, t: Translate): string {
  if (value === null || value === undefined || value === "") return "—";
  switch (def.dataType) {
    case "BOOLEAN":
      return value === true || value === "true" ? t("common.yes", "Da") : t("common.no", "Ne");
    case "NUMBER":
      return def.unit ? `${value} ${def.unit}` : String(value);
    case "DATE":
      return formatDate(String(value));
    case "RANGE":
      return isRangeValue(value) ? formatRange(value, def.unit) : String(value);
    default:
      return String(value);
  }
}

/** The description of a list value as stored on the codebook attribute; null for anything else. */
export function enumDescription(def: AttributeDefView, value: AttributeValue | undefined): string | null {
  if (def.dataType !== "ENUM" || typeof value !== "string") return null;
  return def.enumOptions?.find((o) => o.value === value)?.description ?? null;
}

/**
 * Compact read-only grid of attribute values (label above value; a list value carries
 * its description under it, Strahinja 2026-10-09). The card that owns it switches to
 * `AttributeValuesForm` only after "Izmeni" (Strahinja, 2026-09-10: nothing editable by
 * accident).
 */
export function AttributeValuesView({ defs, values }: { defs: AttributeDefView[]; values: Record<string, AttributeValue> }) {
  const { t } = useTranslation();
  return (
    <dl className="kv-grid">
      {defs.map((d) => {
        const text = formatAttributeValue(d, values[d.key], t);
        const description = enumDescription(d, values[d.key]);
        return (
          <div key={d.id} className="kv-grid-item">
            <dt className="field-label">
              {d.label}
              {d.note && <InfoPopover text={d.note} label={d.label} />}
            </dt>
            <dd className={text === "—" ? "muted" : undefined}>
              {text}
              {description && <div className="muted enum-description">{description}</div>}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}
